import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Book, Items, Cycle, BookEditions } from '@/api/entities';
import { UploadFile } from '@/api/integrations';
import { resolveAssetUrl } from '@/api/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PlusCircle, Trash2, Upload, Image } from 'lucide-react';

const initialFormState = {
  title: '',
  author: '',
  cycle_id: null,
  description: '',
};

    const initialEditionState = { 
        isbn: '', 
        edition_name: '', 
        cover_image_url: '', 
        pages: '', 
        price: '', 
        format: '' 
    };

export default function BookForm({
  isOpen, onClose, book, cycles, onSave, bookEditions: initialBookEditions }) {
  const { t } = useTranslation();
    const [formData, setFormData] = useState(initialFormState);
    const [bookEditions, setBookEditions] = useState([]);
    const [showNewEditionForm, setShowNewEditionForm] = useState(false);
    const [newEdition, setNewEdition] = useState(initialEditionState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);

    useEffect(() => {
        console.log('BookForm useEffect triggered:', { isOpen, book, initialBookEditions });
        if (isOpen) {
            if (book) {
                setFormData({
                    title: book.title || '',
                    author: book.author || '',
                    cycle_id: book.cycle_id || null,
                    description: book.description || '',
                });
                console.log('Setting book editions in BookForm:', initialBookEditions);
                setBookEditions(initialBookEditions || []);
            } else {
                setFormData(initialFormState);
                setBookEditions([]);
            }
            setShowNewEditionForm(false);
            setNewEdition(initialEditionState);
        }
    }, [isOpen, book, initialBookEditions]);

    const handleBookSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        console.log('handleBookSubmit called with bookEditions:', bookEditions);

        try {
            let savedBook;
            if (book) {
                await Book.update(book.id, formData);
                savedBook = { ...book, ...formData };
            } else {
                savedBook = await Book.create(formData);
            }

            console.log('Book saved/updated:', savedBook);

            // Сохраняем или обновляем издания
            console.log('Processing editions, count:', bookEditions.length);
            console.log('Full bookEditions array:', JSON.stringify(bookEditions, null, 2));
            for (const edition of bookEditions) {
                console.log('Processing edition:', edition);
                console.log('Edition metadata:', edition.metadata);
                const editionData = {
                    name: `${savedBook.title} - ${edition.metadata?.edition_name || edition.edition_name || 'Основное издание'}`,
                    type: 'book_edition',
                    book_id: savedBook.id,
                    // Добавляем все поля как top-level для правильного сохранения
                    isbn: edition.metadata?.isbn || edition.isbn,
                    edition_name: edition.metadata?.edition_name || edition.edition_name || 'Основное издание',
                    cover_image_url: edition.metadata?.cover_image_url || edition.cover_image_url || '',
                    pages: edition.metadata?.pages || edition.pages || null,
                    price: edition.metadata?.price || edition.price || null,
                    format: edition.metadata?.format || edition.format || null,
                    metadata: {
                        isbn: edition.metadata?.isbn || edition.isbn,
                        edition_name: edition.metadata?.edition_name || edition.edition_name || 'Основное издание',
                        cover_image_url: edition.metadata?.cover_image_url || edition.cover_image_url || '',
                        pages: edition.metadata?.pages || edition.pages || null,
                        price: edition.metadata?.price || edition.price || null,
                        format: edition.metadata?.format || edition.format || null,
                        book_id: savedBook.id,
                        type: 'book_edition'
                    }
                };

                console.log('Edition data to save:', editionData);

                if (edition.id) {
                    console.log('Updating existing edition:', edition.id);
                    await Items.update(edition.id, editionData);
                } else {
                    console.log('Creating new edition');
                    const result = await Items.create(editionData);
                    console.log('Edition created:', result);
                }
            }

            onSave();
        } catch (error) {
            console.error('Error saving book:', error);
            alert(`Ошибка при сохранении книги: ${error.message}`);
        }
        
        setIsSubmitting(false);
    };

    const handleAddEdition = async () => {
        if (!newEdition.isbn.trim()) {
            alert('Пожалуйста, укажите ISBN для издания');
            return;
        }

        const editionToAdd = {
            ...newEdition,
            edition_name: newEdition.edition_name || 'Основное издание',
            // Добавляем все поля как top-level
            isbn: newEdition.isbn,
            pages: newEdition.pages || null,
            price: newEdition.price || null,
            format: newEdition.format || null,
            cover_image_url: newEdition.cover_image_url || '',
            metadata: {
                isbn: newEdition.isbn,
                edition_name: newEdition.edition_name || 'Основное издание',
                cover_image_url: newEdition.cover_image_url || '',
                pages: newEdition.pages || null,
                price: newEdition.price || null,
                format: newEdition.format || null
            }
        };

        setBookEditions(prev => [...prev, editionToAdd]);
        setNewEdition(initialEditionState);
        setShowNewEditionForm(false);
    };

    const handleRemoveEdition = async (index) => {
        const edition = bookEditions[index];
        
        try {
            if (edition.id) {
                // Если это существующее издание, удаляем из БД
                await BookEditions.delete(edition.id);
                console.log('Edition deleted from database:', edition.id);
            }
            
            // Обновляем локальное состояние
            setBookEditions(prev => prev.filter((_, i) => i !== index));
            
        } catch (error) {
            console.error('Error deleting edition:', error);
            alert(`Ошибка при удалении издания: ${error.message}`);
        }
    };

    const handleImageUpload = async (file, isForNewEdition = false, index = null) => {
        setUploadingCover(true);
        try {
            const result = await UploadFile({ file });
            const resolvedUrl = await resolveAssetUrl(result.file_url);
            
            if (!result.success) {
                throw new Error(result.error || 'Upload failed');
            }
            
            if (isForNewEdition) {
                setNewEdition(prev => ({ ...prev, cover_image_url: resolvedUrl }));
            } else if (index !== null) {
                // Обновляем локальное состояние
                setBookEditions(prev => prev.map((edition, i) => 
                    i === index 
                        ? { 
                            ...edition, 
                            metadata: {
                                ...edition.metadata,
                                cover_image_url: resolvedUrl
                            }
                        }
                        : edition
                ));
                
                // Если это существующее издание (с ID), сразу сохраняем в БД
                const edition = bookEditions[index];
                if (edition.id) {
                    const editionData = {
                        name: edition.name,
                        type: edition.type,
                        book_id: edition.book_id,
                        metadata: {
                            ...edition.metadata,
                            cover_image_url: resolvedUrl
                        }
                    };
                    
                    await Items.update(edition.id, editionData);
                    console.log('Cover image saved for existing edition:', edition.id);
                }
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            alert(`Ошибка при загрузке изображения: ${error.message}`);
        }
        setUploadingCover(false);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white" aria-describedby="book-form-description">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-slate-900">
                        {book ? 'Редактировать книгу' : 'Добавить новую книгу'}
                    </DialogTitle>
                    <DialogDescription id="book-form-description">
                        Заполните информацию о книге и её изданиях
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleBookSubmit} className="space-y-6">
                    <Card className="border-slate-200 bg-white">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-lg">Основная информация</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <Label htmlFor="title">Название *</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                    required
                                    className="bg-white"
                                />
                            </div>

                            <div>
                                <Label htmlFor="author">Автор *</Label>
                                <Input
                                    id="author"
                                    value={formData.author}
                                    onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                                    required
                                    className="bg-white"
                                />
                            </div>

                            <div>
                                <Label htmlFor="cycle">Серия</Label>
                                <Select 
                                    value={formData.cycle_id || 'none'} 
                                    onValueChange={(value) => setFormData(prev => ({ 
                                        ...prev, 
                                        cycle_id: value === 'none' ? null : value 
                                    }))}
                                >
                                    <SelectTrigger className="bg-white">
                                        <SelectValue placeholder="Без серии" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white">
                                        <SelectItem value="none">Без серии</SelectItem>
                                        {cycles.map((cycle) => (
                                            <SelectItem key={cycle.id} value={cycle.id}>
                                                {cycle.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="description">Описание</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    className="h-24 bg-white"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200 bg-white">
                        <CardHeader className="pb-4 flex flex-row items-center justify-between">
                            <CardTitle className="text-lg">Издания книги</CardTitle>
                            <Button 
                                type="button" 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setShowNewEditionForm(true)}
                                className="bg-white"
                            >
                                <PlusCircle className="w-4 h-4 mr-2" />
                                Добавить издание
                            </Button>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {bookEditions.map((edition, index) => (
                                <div key={index} className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex-1 space-y-3">
                                            <div>
                                                <Label htmlFor={`edition-name-${index}`}>Название издания</Label>
                                                <Input
                                                    id={`edition-name-${index}`}
                                                    value={edition.metadata?.edition_name || edition.edition_name || ''}
                                                    onChange={async (e) => {
                                                        const newValue = e.target.value;
                                                        setBookEditions(prev => prev.map((ed, i) => 
                                                            i === index ? { 
                                                                ...ed, 
                                                                metadata: {
                                                                    ...ed.metadata,
                                                                    edition_name: newValue
                                                                }
                                                            } : ed
                                                        ));
                                                        
                                                        // Если это существующее издание, сразу сохраняем в БД
                                                        const edition = bookEditions[index];
                                                        if (edition.id) {
                                                            try {
                                                                const editionData = {
                                                                    name: edition.name,
                                                                    type: edition.type,
                                                                    book_id: edition.book_id,
                                                                    metadata: {
                                                                        ...edition.metadata,
                                                                        edition_name: newValue
                                                                    }
                                                                };
                                                                
                                                                await Items.update(edition.id, editionData);
                                                                console.log('Edition name saved:', newValue);
                                                            } catch (error) {
                                                                console.error('Error saving edition name:', error);
                                                            }
                                                        }
                                                    }}
                                                    placeholder="Основное издание, Подарочное и т.д."
                                                    className="bg-white"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor={`edition-isbn-${index}`}>ISBN</Label>
                                                <Input
                                                    id={`edition-isbn-${index}`}
                                                    value={edition.metadata?.isbn || edition.isbn || ''}
                                                    onChange={async (e) => {
                                                        const newValue = e.target.value;
                                                        console.log('Setting ISBN in local state:', newValue);
                                                        setBookEditions(prev => {
                                                            const updated = prev.map((ed, i) => 
                                                                i === index ? { 
                                                                    ...ed, 
                                                                    metadata: {
                                                                        ...ed.metadata,
                                                                        isbn: newValue
                                                                    }
                                                                } : ed
                                                            );
                                                            console.log('Updated bookEditions:', updated);
                                                            return updated;
                                                        });
                                                        
                                                        // Если это существующее издание, сразу сохраняем в БД
                                                        const edition = bookEditions[index];
                                                        if (edition.id) {
                                                            try {
                                                                const editionData = {
                                                                    name: edition.name,
                                                                    type: edition.type,
                                                                    book_id: edition.book_id,
                                                                    metadata: {
                                                                        ...edition.metadata,
                                                                        isbn: newValue
                                                                    }
                                                                };
                                                                
                                                                await Items.update(edition.id, editionData);
                                                                console.log('Edition ISBN saved:', newValue);
                                                            } catch (error) {
                                                                console.error('Error saving edition ISBN:', error);
                                                            }
                                                        }
                                                    }}
                                                    placeholder="978-5-XXXXX-XXX-X"
                                                    className="bg-white"
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRemoveEdition(index)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    
                                    <div className="flex items-center gap-4">
                                        {(edition.metadata?.cover_image_url || edition.cover_image_url) ? (
                                            <div className="relative">
                                                <img 
                                                    src={edition.metadata?.cover_image_url || edition.cover_image_url} 
                                                    alt="Обложка" 
                                                    className="w-20 h-28 object-cover rounded border"
                                                    onError={(e) => {
                                                        console.error('Failed to load cover image:', edition.cover_image_url);
                                                        e.target.style.display = 'none';
                                                    }}
                                                    onLoad={() => {
                                                        console.log('Cover image loaded successfully:', edition.cover_image_url);
                                                    }}
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="absolute top-0 right-0 bg-white/80 hover:bg-white text-red-500 hover:text-red-700 p-1"
                                                    onClick={() => setBookEditions(prev => prev.map((ed, i) => 
                                                        i === index ? { 
                                                            ...ed, 
                                                            metadata: {
                                                                ...ed.metadata,
                                                                cover_image_url: ''
                                                            }
                                                        } : ed
                                                    ))}
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="w-20 h-28 border-2 border-dashed border-slate-300 rounded flex items-center justify-center bg-white">
                                                <Image className="w-8 h-8 text-slate-400" />
                                            </div>
                                        )}
                                        
                                        <div>
                                            <Label htmlFor={`cover-${index}`} className="cursor-pointer">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    disabled={uploadingCover}
                                                    className="bg-white"
                                                    onClick={() => document.getElementById(`cover-${index}`).click()}
                                                >
                                                    <Upload className="w-4 h-4 mr-2" />
                                                    {uploadingCover ? 'Загрузка...' : 'Загрузить обложку'}
                                                </Button>
                                            </Label>
                                            <Input
                                                id={`cover-${index}`}
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        handleImageUpload(file, false, index);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {showNewEditionForm && (
                                <div className="p-4 border-2 border-dashed border-slate-300 rounded-lg bg-white">
                                    <h4 className="font-medium text-slate-900 mb-3">Новое издание</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <Label htmlFor="new-isbn">ISBN *</Label>
                                            <Input
                                                id="new-isbn"
                                                value={newEdition.isbn}
                                                onChange={(e) => setNewEdition(prev => ({ ...prev, isbn: e.target.value }))}
                                                placeholder="978-5-XXXXX-XXX-X"
                                                className="bg-white"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="new-edition-name">Название издания</Label>
                                            <Input
                                                id="new-edition-name"
                                                value={newEdition.edition_name}
                                                onChange={(e) => setNewEdition(prev => ({ ...prev, edition_name: e.target.value }))}
                                                placeholder="Основное издание, Подарочное и т.д."
                                                className="bg-white"
                                            />
                                        </div>
                                        
                                        <div className="grid grid-cols-3 gap-3">
                                            <div>
                                                <Label htmlFor="new-pages">Страниц</Label>
                                                <Input
                                                    id="new-pages"
                                                    type="number"
                                                    value={newEdition.pages}
                                                    onChange={(e) => setNewEdition(prev => ({ ...prev, pages: e.target.value }))}
                                                    placeholder="0"
                                                    className="bg-white"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="new-price">Цена</Label>
                                                <Input
                                                    id="new-price"
                                                    type="number"
                                                    step="0.01"
                                                    value={newEdition.price}
                                                    onChange={(e) => setNewEdition(prev => ({ ...prev, price: e.target.value }))}
                                                    placeholder="0.00"
                                                    className="bg-white"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="new-format">Формат</Label>
                                                <Input
                                                    id="new-format"
                                                    value={newEdition.format}
                                                    onChange={(e) => setNewEdition(prev => ({ ...prev, format: e.target.value }))}
                                                    placeholder="A4, A5, etc."
                                                    className="bg-white"
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-4">
                                            {newEdition.cover_image_url ? (
                                                <div className="relative">
                                                    <img 
                                                        src={newEdition.cover_image_url} 
                                                        alt="Обложка" 
                                                        className="w-20 h-28 object-cover rounded border"
                                                        onError={(e) => {
                                                            console.error('Failed to load new edition cover image:', newEdition.cover_image_url);
                                                            e.target.style.display = 'none';
                                                        }}
                                                        onLoad={() => {
                                                            console.log('New edition cover image loaded successfully:', newEdition.cover_image_url);
                                                        }}
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="absolute top-0 right-0 bg-white/80 hover:bg-white text-red-500 hover:text-red-700 p-1"
                                                        onClick={() => setNewEdition(prev => ({ ...prev, cover_image_url: '' }))}
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="w-20 h-28 border-2 border-dashed border-slate-300 rounded flex items-center justify-center bg-slate-50">
                                                    <Image className="w-8 h-8 text-slate-400" />
                                                </div>
                                            )}
                                            
                                            <div>
                                                <Label htmlFor="new-cover" className="cursor-pointer">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        disabled={uploadingCover}
                                                        className="bg-white"
                                                        onClick={() => document.getElementById('new-cover').click()}
                                                    >
                                                        <Upload className="w-4 h-4 mr-2" />
                                                        {uploadingCover ? 'Загрузка...' : 'Загрузить обложку'}
                                                    </Button>
                                                </Label>
                                                <Input
                                                    id="new-cover"
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                            handleImageUpload(file, true);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="flex gap-2 pt-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    setShowNewEditionForm(false);
                                                    setNewEdition(initialEditionState);
                                                }}
                                                className="bg-white"
                                            >
                                                Отмена
                                            </Button>
                                            <Button
                                                type="button"
                                                size="sm"
                                                onClick={handleAddEdition}
                                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                            >
                                                Добавить издание
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {bookEditions.length === 0 && !showNewEditionForm && (
                                <div className="text-center py-8 text-slate-500">
                                    <p>Пока нет изданий для этой книги</p>
                                    <p className="text-sm">Добавьте издание для указания ISBN и обложки</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <div className="flex justify-between items-center pt-6 border-t bg-white">
                        <Button type="button" variant="outline" onClick={onClose} className="bg-white">
                            Отмена
                        </Button>
                        <Button 
                            type="submit" 
                            disabled={isSubmitting || !formData.title || !formData.author}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                            {isSubmitting ? 'Сохранение...' : 'Сохранить книгу'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}